import { type AuthProps } from '../../../public/site/AuthProvider.js';
import { type OpenFormAccount, useOpenFormAccount } from '../OpenFormHooks/useOpenFormAccount.js';
import { Outlet, useLocation } from 'react-router-dom';
import { type RealAuthenticatedUserState } from '../../../common/types/states.js';
import { concat } from '../OpenFormUtils.js';
import { incompleteOrdersMsg, salesFormsMsg, t } from '../../../common/i18n/index.js';
import { isPreview, useOpenFormGoto } from '../OpenFormHooks/useOpenFormGoto.js';
import { paths } from '../../../common/constants/pathVariables.js';
import { useCallback, useMemo } from 'react';
import { useEventListener } from '../OpenFormHooks/useEventListener.js';
import { useOpenFormCanvas, useOpenFormState } from '../OpenFormProvider.js';
import { useReload } from '../OpenFormHooks/useReload.js';

export type OpenFormOutlet = {
  account?: OpenFormAccount;
  preview?: boolean;
  reload?: () => void;
  title?: (path?: string) => string | undefined;
  user?: RealAuthenticatedUserState;
};

export const OpenFormLayoutOutlet = ({ auth }: { auth: AuthProps }) => {
  const { pathname } = useLocation();
  const { form } = useOpenFormState();
  const canvas = useOpenFormCanvas();
  const goto = useOpenFormGoto();
  const account = useOpenFormAccount();
  const preview = useMemo(() => isPreview(pathname), [pathname]);
  const [reloading, reload] = useReload();

  const title = useCallback(
    (path = pathname) => {
      switch (path) {
        case paths.OPENFORM_LIST_DRAFT:
          return t.E99N(incompleteOrdersMsg);
        case paths.OPENFORM_LIST_FORM:
          return t.QF2O(salesFormsMsg);
        default:
          return form?.name && concat(form.name, form.referenceNumber).join(' • ');
      }
    },
    [pathname, form]
  );

  useEventListener(
    'message',
    useCallback(
      ({ data }: MessageEvent) => {
        switch (data?.action) {
          case 'billingAccountCreated':
          case 'contactCreated':
            return canvas.postMessageAcknowledge(data.action); // Placeholder
          case 'goto':
            return goto(data);
        }
      },
      [canvas, goto]
    )
  );

  return reloading ? null : <Outlet context={{ account, preview, reload, title, user: auth.authenticatedUser }} />;
};
